// Unread reply markers shared by the dealer dialog and the admin list

import React from 'react';
import { cn } from '@/lib/utils';

/** Row styling for a conversation with unread replies: accent edge + light tint */
export const unreadRowCls = (unreadCount: number) =>
  unreadCount > 0 ? 'border-l-4 border-l-primary bg-primary/5' : undefined;

/** Small dot, e.g. next to a tab label that contains unread conversations */
export const UnreadDot: React.FC<{ className?: string }> = ({ className }) => (
  <span
    className={cn('inline-block h-2 w-2 rounded-full bg-primary shrink-0', className)}
    aria-hidden='true'
  />
);

/** "N new" pill for a conversation row */
export const UnreadPill: React.FC<{ count: number }> = ({ count }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5',
      'text-[11px] font-semibold text-primary-foreground shrink-0'
    )}
    aria-label={`${count} new ${count === 1 ? 'reply' : 'replies'}`}
  >
    {count} new
  </span>
);

/** Conversation title: bold with a dot and "N new" pill when it has unread replies */
export const UnreadTitle: React.FC<{ title: string; unreadCount: number; className?: string }> = ({
  title,
  unreadCount,
  className
}) => (
  <h4
    className={cn(
      'leading-5 flex-1 min-w-0 flex items-center gap-1.5',
      unreadCount > 0 ? 'font-bold' : 'font-semibold',
      className
    )}
  >
    {unreadCount > 0 && <UnreadDot />}
    <span className='min-w-0'>{title}</span>
    {unreadCount > 0 && <UnreadPill count={unreadCount} />}
  </h4>
);

/** Divider shown above the first reply the user has not seen yet */
export const NewMessagesDivider: React.FC = () => (
  <div
    className='flex items-center gap-2 text-xs font-semibold text-primary'
    role='separator'
    aria-label='New replies'
  >
    <div className='h-px flex-1 bg-primary/40' />
    New
    <div className='h-px flex-1 bg-primary/40' />
  </div>
);
